export interface Beach {
  id: string;
  name: string;
  area: string;
  lat: number;
  lon: number;
  /** azimuth the beach opens toward (deg, met. convention: waves FROM that direction hit head-on) */
  facing: number;
  /** marine grid query point, nudged offshore so the 5 km wave grid doesn't snap to a land cell */
  mLat: number;
  mLon: number;
}

type BeachSeed = Omit<Beach, 'mLat' | 'mLon'>;

// Ordered west -> east along the coast. Coordinates are OSM-sourced (Overpass
// `natural=beach` / Nominatim), not eyeballed. Facings for the south-coast
// beaches below were derived from the local coastline tangent, taking the
// seaward normal; the same method reproduces Mackenzie's hand-set 115 as 119,
// which is what makes it trustworthy enough to use.
const SEEDS: BeachSeed[] = [
  { id: 'kalymnos',    name: 'Kalymnos Beach',  area: 'Pentakomo',  lat: 34.7179, lon: 33.2755, facing: 170 },
  { id: 'zygi',        name: 'Marinoparea',     area: 'Zygi',       lat: 34.7285, lon: 33.3406, facing: 170 },
  { id: 'maroni',      name: 'Secret Paradise', area: 'Maroni',     lat: 34.7315, lon: 33.3645, facing: 155 },
  { id: 'alaminos',    name: 'Alaminos Beach',  area: 'Alaminos',   lat: 34.7750, lon: 33.4700, facing: 155 },
  // Faros sits on the Cape Kiti hinge, where the coast swings from ~120 to ~155.
  // Least certain facing in the table — recalibrate this one first.
  { id: 'faros',       name: 'Faros Beach',     area: 'Pervolia',   lat: 34.8194, lon: 33.6040, facing: 145 },
  { id: 'finikoudes',  name: 'Finikoudes',      area: 'Larnaca',    lat: 34.9110, lon: 33.6390, facing: 100 },
  { id: 'mackenzie',   name: 'Mackenzie Beach', area: 'Larnaca',    lat: 34.8735, lon: 33.6400, facing: 115 },
  { id: 'dhekelia',    name: 'CTO / Dhekelia',  area: 'Dhekelia',   lat: 34.9800, lon: 33.7350, facing: 145 },
  { id: 'thekla',      name: 'Ayia Thekla',     area: 'Sotira',     lat: 34.9790, lon: 33.9120, facing: 180 },
  { id: 'makronissos', name: 'Makronissos',     area: 'Ayia Napa',  lat: 34.9855, lon: 33.9250, facing: 190 },
  { id: 'nissi',       name: 'Nissi Beach',     area: 'Ayia Napa',  lat: 34.9885, lon: 33.9430, facing: 195 },
  { id: 'pantachou',   name: 'Pantachou',       area: 'Ayia Napa',  lat: 34.9840, lon: 34.0050, facing: 170 },
  { id: 'konnos',      name: 'Konnos Bay',      area: 'Cape Greco', lat: 34.9770, lon: 34.0760, facing: 130 },
  { id: 'figtree',     name: 'Fig Tree Bay',    area: 'Protaras',   lat: 35.0125, lon: 34.0580, facing: 95 },
  { id: 'sunrise',     name: 'Sunrise Beach',   area: 'Protaras',   lat: 35.0180, lon: 34.0555, facing: 90 },
  { id: 'louma',       name: 'Louma / Pernera', area: 'Pernera',    lat: 35.0300, lon: 34.0450, facing: 75 },
];

/** nudge the marine query point ~0.045° (~5 km) offshore along the facing azimuth */
const OFFSHORE_DEG = 0.045;

export const BEACHES: Beach[] = SEEDS.map((b) => {
  const a = (b.facing * Math.PI) / 180;
  return {
    ...b,
    mLat: +(b.lat + OFFSHORE_DEG * Math.cos(a)).toFixed(4),
    mLon: +(b.lon + (OFFSHORE_DEG * Math.sin(a)) / Math.cos((b.lat * Math.PI) / 180)).toFixed(4),
  };
});
